import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dispatch, SetStateAction, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import ToastContext from '../../../../../context/ToastContext';
import useAxiosHook from '../../../../../hooks/useAxiosHook';
import usePrevious from '../../../../../hooks/usePrevious';
import { Numeric } from '../../../../../types/general';
import {
  errorToast,
  successToast,
} from '../../../../../utils/helpers/primereact';
import { generateErrorMsg } from '../../../Orders/DeletedOrders/DeletedOrders.functions';
import { LogisticsRoute } from '../../LogisticsRoutes.functions';

type Props = {
  visible: boolean;
  data: LogisticsRoute[];
  onHide: () => void;
  reloadLogisticsRoute: () => void;
  setSelectionMultiple: Dispatch<SetStateAction<any[]>>;
  setContextMenuSelection: Dispatch<SetStateAction<LogisticsRoute | undefined>>;
};

function DeleteDialog({
  visible,
  data,
  onHide,
  reloadLogisticsRoute,
  setSelectionMultiple,
  setContextMenuSelection,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const { toastRef } = useContext(ToastContext);
  const {
    data: deleteData,
    error: deleteError,
    reload: deleteReload,
    isLoading: isDeleteLoading,
  } = useAxiosHook<undefined, { error?: string }>();

  function handleDeleteLogisticRoutes(LogisticsRouteId: Numeric) {
    if (!LogisticsRouteId) {
      return;
    }
    deleteReload({
      url: `/logistics-route/${LogisticsRouteId}`,
      method: 'DELETE',
    });
  }

  const prevDeleteError = usePrevious(deleteError);
  const prevDeleteData = usePrevious(deleteData);

  useEffect(() => {
    if (!deleteData || deleteData === prevDeleteData) {
      return;
    }

    if (toastRef?.current) {
      successToast(
        toastRef,
        t('Success'),
        data?.length > 1
          ? t('Logistic routes successfully deleted.')
          : t('Logistic route {{name}} was successfully deleted.', {
              name: data[0]?.name ?? '',
            })
      );
    }
    onHide();
    reloadLogisticsRoute();
    setSelectionMultiple([]);
    setContextMenuSelection(undefined);
  }, [
    data,
    deleteData,
    prevDeleteData,
    onHide,
    reloadLogisticsRoute,
    setSelectionMultiple,
    setContextMenuSelection,
    t,
    toastRef,
  ]);

  useEffect(() => {
    if (!deleteError || deleteError === prevDeleteError) {
      return;
    }
    const errText = deleteError?.response?.data?.error;

    if (toastRef?.current) {
      errorToast(
        toastRef,
        t('Error'),
        data?.length > 1
          ? t('Failed group deleting. {{errorMessage}}', {
              errorMessage: errText ? generateErrorMsg(t, errText) : '',
            })
          : t('Deleting logistic route {{name}} failed. {{errorMessage}}.', {
              name: data[0]?.name ?? '',
              errorMessage: errText ? generateErrorMsg(t, errText) : '',
            })
      );
    }

    onHide();
  }, [data, deleteError, onHide, prevDeleteError, t, toastRef]);

  function handleDeleteSubmission() {
    if (!data?.length) {
      return;
    }
    data.forEach((o) => handleDeleteLogisticRoutes(o.id));
  }

  const dialogFooter = (
    <div className='className="p-pt-4"'>
      <Button
        type="button"
        label={t('Close')}
        onClick={() => onHide()}
        className="p-button-secondary p-button-text"
      />

      <Button
        type="button"
        label={isDeleteLoading ? t('Deleting...') : t('Delete')}
        disabled={isDeleteLoading || data.length === 0}
        onClick={() => {
          handleDeleteSubmission();
        }}
        className="p-button-danger"
        data-cy="submit-btn"
      />
    </div>
  );

  return (
    <Dialog
      header={
        data?.length > 1
          ? t('Delete logistic routes')
          : t('Delete logistic route')
      }
      footer={dialogFooter}
      visible={visible}
      resizable={false}
      onHide={onHide}
      style={{ width: 480, maxWidth: '100%' }}
    >
      {data?.length > 0 && (
        <p>
          {data?.length > 1 ? (
            t('Are you sure you want to delete this group of logistic routes?')
          ) : (
            <>
              {t(
                'Are you sure you want to delete the following logistic route?'
              )}{' '}
              <b>{data[0]?.name}</b>
            </>
          )}
          <br />
          <br />
          {t('This action cannot be undone.')}
        </p>
      )}
    </Dialog>
  );
}

export default DeleteDialog;
